import { useCallback, useEffect, useRef } from 'react';
import { isRecord } from '../util/type-guards.ts';
import { formatTurnFragment, parseTurnFragment, resolveHistoryAction } from './turn-url.ts';

/** Coalesces rail scrubs and restores turn selection through the browser history. */
export function useTurnHistory({
  turnCount,
  shownIndex,
  onSelect,
}: {
  turnCount: number;
  shownIndex: number;
  onSelect: (index: number) => void;
}) {
  const fromPopstate = useRef(false);
  const pendingRestore = useRef(parseTurnFragment(window.location.hash));
  const syncTimer = useRef<number | undefined>(undefined);
  const clearRestore = useCallback(() => {
    pendingRestore.current = null;
  }, []);

  useEffect(() => {
    if (pendingRestore.current !== null && turnCount > pendingRestore.current) {
      const target = pendingRestore.current;
      pendingRestore.current = null;
      fromPopstate.current = true;
      onSelect(target);
    }
  }, [turnCount, onSelect]);

  useEffect(() => {
    const onPopstate = (event: PopStateEvent) => {
      const state: unknown = event.state;
      if (!isRecord(state) || typeof state.turn !== 'number') {
        return;
      }
      pendingRestore.current = null;
      fromPopstate.current = true;
      onSelect(Math.min(Math.max(state.turn, 0), turnCount - 1));
    };
    window.addEventListener('popstate', onPopstate);
    return () => window.removeEventListener('popstate', onPopstate);
  }, [turnCount, onSelect]);

  useEffect(() => {
    if (shownIndex < 0 || pendingRestore.current !== null) {
      return;
    }
    window.clearTimeout(syncTimer.current);
    syncTimer.current = window.setTimeout(() => {
      const state: unknown = window.history.state;
      const urlIndex = isRecord(state) && typeof state.turn === 'number' ? state.turn : undefined;
      const action = resolveHistoryAction({
        urlIndex,
        shownIndex,
        fromPopstate: fromPopstate.current,
      });
      fromPopstate.current = false;
      if (action === 'none') {
        return;
      }
      const url =
        window.location.pathname + window.location.search + formatTurnFragment(shownIndex);
      if (action === 'replace') {
        window.history.replaceState({ turn: shownIndex }, '', url);
      } else {
        window.history.pushState({ turn: shownIndex }, '', url);
      }
    }, 250);
    return () => window.clearTimeout(syncTimer.current);
  }, [shownIndex]);
  return clearRestore;
}
